import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useQueryParam, NumberParam } from 'use-query-params'

import { useStore } from './useStore'
import { useAuthHydrationReady } from './useAuthHydrationReady'
import { fetchInsuranceDocument } from './useInsuranceDocumentQuery'
import {
  fetchCoverageRequirements,
  fetchMultiCollatteral,
  collectUniqueLoanIds,
} from './useGetCoverageRequirements'

import { filterItemsBySearchTerm } from '../utils/dashboardSearchUtils'
import {
  coverageQueriesHaveUnmetRequirements,
  coverageQueriesHaveCancelledInsurance,
} from '../utils/dashboardCoverageAlertUtils'
import { filterDashboardItemsByAdditionalCoverages } from '../utils/dashboardAdditionalCoveragesUtils'

const AGENCY_ID = 1
const PAGE_SIZE = 5
const MAX_RECENT_SEARCHES = 10

export const getRefIdFromDashboardItem = item =>
  String(item?.ReferenceID ?? item?.ReferenceId ?? '')

const buildDataByRefId = (refIds, queries) => {
  const map = {}

  refIds.forEach((refId, index) => {
    const data = queries[index]?.data
    if (refId && data) {
      map[refId] = data
    }
  })

  return map
}

const getIsResolvingDashboardItems = ({
  profileRefIds,
  allProfileCoverageResolved,
  profileCoverageQueries,
  loanIds,
  isMultiCollateralLoading,
  isMultiCollateralFetched,
  multiCollateralData,
  multiCollateralRefIds,
  allMultiCollateralCoverageResolved,
}) =>
  profileRefIds.length > 0 &&
  (!allProfileCoverageResolved ||
    profileCoverageQueries.some(query => query.isLoading) ||
    (loanIds.length > 0 &&
      (isMultiCollateralLoading ||
        (!isMultiCollateralFetched && !multiCollateralData) ||
        (multiCollateralRefIds.length > 0 && !allMultiCollateralCoverageResolved))))

export const useDashboardPage = () => {
  const authHydrationReady = useAuthHydrationReady()
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [dismissedUnmetMessage, setDismissedUnmetMessage] = useState(false)
  const [dismissedCanceledMessage, setDismissedCanceledMessage] = useState(
    false
  )
  const [needHelp, setNeedHelp] = useState(false)
  const [activeSearchTerm, setActiveSearchTerm] = useState(null)
  const [searchInputValue, setSearchInputValue] = useState('')
  const [recentSearches, setRecentSearches] = useState([])
  const activeSearchTermRef = useRef(null)
  const { dispatch, state } = useStore()
  const { userData } = state
  const profileRefIds = useMemo(
    () => (Array.isArray(userData?.RefId) ? userData.RefId.map(String) : []),
    [userData]
  )
  const profileKey = profileRefIds.join(',')
  const isAuthenticated = authHydrationReady && !!state?.hasSessionToken

  const [pageParam, setPageParam] = useQueryParam('page', NumberParam)

  const profileCoverageQueries = useQueries({
    queries: profileRefIds.map(refId => ({
      queryKey: ['coverageRequirements', refId, AGENCY_ID],
      queryFn: () => fetchCoverageRequirements(refId, AGENCY_ID),
      enabled: isAuthenticated && !!refId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    })),
  })

  const allProfileCoverageResolved =
    profileRefIds.length === 0 ||
    profileCoverageQueries.every(query => query.isFetched)

  const loanIds = useMemo(
    () =>
      collectUniqueLoanIds(
        profileCoverageQueries.map(query => query.data).filter(Boolean)
      ),
    [profileCoverageQueries]
  )

  const {
    data: multiCollateralData,
    isLoading: isMultiCollateralLoading,
    isFetched: isMultiCollateralFetched,
  } = useQuery({
    queryKey: ['multiCollateral', loanIds],
    queryFn: () => fetchMultiCollatteral(loanIds),
    enabled:
      isAuthenticated && allProfileCoverageResolved && loanIds.length > 0,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  })

  const multiCollateralRefItems = useMemo(() => {
    if (!Array.isArray(multiCollateralData)) return []

    return multiCollateralData.filter(item => getRefIdFromDashboardItem(item))
  }, [multiCollateralData])

  const multiCollateralRefIds = useMemo(
    () =>
      multiCollateralRefItems
        .map(item => getRefIdFromDashboardItem(item))
        .filter(Boolean),
    [multiCollateralRefItems]
  )

  const multiCollateralCoverageQueries = useQueries({
    queries: multiCollateralRefIds.map(refId => ({
      queryKey: ['coverageRequirements', refId, AGENCY_ID],
      queryFn: () => fetchCoverageRequirements(refId, AGENCY_ID),
      enabled: !!refId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
    })),
  })

  const allMultiCollateralCoverageResolved =
    multiCollateralRefIds.length === 0 ||
    multiCollateralCoverageQueries.every(query => query.isFetched)

  const coverageByRefId = useMemo(
    () => buildDataByRefId(multiCollateralRefIds, multiCollateralCoverageQueries),
    [multiCollateralRefIds, multiCollateralCoverageQueries]
  )

  const dashboardRefItems = useMemo(() => {
    if (!allMultiCollateralCoverageResolved) return []

    return filterDashboardItemsByAdditionalCoverages(
      multiCollateralRefItems,
      coverageByRefId,
      getRefIdFromDashboardItem
    )
  }, [
    allMultiCollateralCoverageResolved,
    multiCollateralRefItems,
    coverageByRefId,
  ])

  const dashboardItemsKey = useMemo(
    () =>
      dashboardRefItems.map(item => getRefIdFromDashboardItem(item)).join(','),
    [dashboardRefItems]
  )

  const allDashboardRefIds = useMemo(
    () =>
      dashboardRefItems
        .map(item => getRefIdFromDashboardItem(item))
        .filter(Boolean),
    [dashboardRefItems]
  )

  const isSearchEnabled = dashboardRefItems.length >= 5

  const allInsuranceQueries = useQueries({
    queries: allDashboardRefIds.map(refId => ({
      queryKey: ['insuranceDocument', refId, AGENCY_ID],
      queryFn: () => fetchInsuranceDocument(refId, AGENCY_ID),
      enabled: !!refId && isSearchEnabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
    })),
  })

  const insuranceDataByRefId = useMemo(
    () => buildDataByRefId(allDashboardRefIds, allInsuranceQueries),
    [allDashboardRefIds, allInsuranceQueries]
  )

  const searchableDashboardItems = useMemo(
    () =>
      dashboardRefItems.map(item => {
        const refId = getRefIdFromDashboardItem(item)

        return {
          ...item,
          insuranceDocument: insuranceDataByRefId[refId] ?? null,
        }
      }),
    [dashboardRefItems, insuranceDataByRefId]
  )

  const searchResults = useMemo(() => {
    if (!activeSearchTerm) return null

    return filterItemsBySearchTerm(searchableDashboardItems, activeSearchTerm)
  }, [activeSearchTerm, searchableDashboardItems])

  const displayRefItems = searchResults ?? dashboardRefItems
  const isSearchActive = activeSearchTerm !== null

  const totalPages = Math.max(1, Math.ceil(displayRefItems.length / PAGE_SIZE))
  const currentPage = Math.min(Math.max(pageParam || 1, 1), totalPages)

  const pagedRefItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return displayRefItems.slice(start, start + PAGE_SIZE)
  }, [displayRefItems, currentPage])

  const pagedDashboardRefIds = useMemo(
    () =>
      pagedRefItems
        .map(item => getRefIdFromDashboardItem(item))
        .filter(Boolean),
    [pagedRefItems]
  )

  const clearSearchResults = useCallback(() => {
    setActiveSearchTerm(null)
    setSearchInputValue('')
    setPageParam(undefined, 'replaceIn')
  }, [setPageParam])

  const handleSearchInputCleared = useCallback(
    inputValue => {
      if (!inputValue?.trim() && activeSearchTermRef.current) {
        clearSearchResults()
      }
    },
    [clearSearchResults]
  )

  const handleSearchSubmit = useCallback(
    value => {
      const trimmed = value.trim()

      if (!trimmed) {
        clearSearchResults()
        return
      }

      setRecentSearches(prev => {
        const withoutDuplicate = prev.filter(item => item !== trimmed)
        return [trimmed, ...withoutDuplicate].slice(0, MAX_RECENT_SEARCHES)
      })

      setActiveSearchTerm(trimmed)
      setSearchInputValue(trimmed)
      setPageParam(undefined, 'replaceIn')
    },
    [clearSearchResults, setPageParam]
  )

  const handlePageChange = useCallback(
    nextPage => {
      setPageParam(nextPage === 1 ? undefined : nextPage, 'pushIn')
    },
    [setPageParam]
  )

  const insuranceQueries = useQueries({
    queries: pagedDashboardRefIds.map(refId => ({
      queryKey: ['insuranceDocument', refId, AGENCY_ID],
      queryFn: () => fetchInsuranceDocument(refId, AGENCY_ID),
      enabled: !!refId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
    })),
  })

  // Kept-card coverage queries (subset of multiCollateralCoverageQueries via cache).
  const alertCoverageQueries = useMemo(
    () =>
      allDashboardRefIds
        .map(refId => {
          const index = multiCollateralRefIds.indexOf(refId)
          return index >= 0 ? multiCollateralCoverageQueries[index] : null
        })
        .filter(Boolean),
    [allDashboardRefIds, multiCollateralRefIds, multiCollateralCoverageQueries]
  )

  const pagedCoverageQueries = useQueries({
    queries: pagedDashboardRefIds.map(refId => ({
      queryKey: ['coverageRequirements', refId, AGENCY_ID],
      queryFn: () => fetchCoverageRequirements(refId, AGENCY_ID),
      enabled: !!refId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      keepPreviousData: true,
    })),
  })

  const hasUnmetRequirements = coverageQueriesHaveUnmetRequirements(
    alertCoverageQueries
  )
  const hasCancelled = coverageQueriesHaveCancelledInsurance(
    alertCoverageQueries
  )

  const allInsuranceErrored =
    insuranceQueries.length > 0 &&
    insuranceQueries.every(query => query.isError)
  const allCoverageErrored =
    alertCoverageQueries.length > 0 &&
    alertCoverageQueries.every(query => query.isError)

  // True when every paged RefID would render DashboardErrorCard
  // (insurance OR coverage error), so the page can show one maintenance tile.
  const allRefTilesErrored =
    pagedDashboardRefIds.length > 0 &&
    insuranceQueries.length === pagedDashboardRefIds.length &&
    pagedCoverageQueries.length === pagedDashboardRefIds.length &&
    insuranceQueries.every(query => query.isFetched && !query.isLoading) &&
    pagedCoverageQueries.every(query => query.isFetched && !query.isLoading) &&
    insuranceQueries.every(
      (query, index) =>
        query.isError || pagedCoverageQueries[index]?.isError
    )

  const isResolvingDashboardItems = getIsResolvingDashboardItems({
    profileRefIds,
    allProfileCoverageResolved,
    profileCoverageQueries,
    loanIds,
    isMultiCollateralLoading,
    isMultiCollateralFetched,
    multiCollateralData,
    multiCollateralRefIds,
    allMultiCollateralCoverageResolved,
  })

  const isHeaderDataLoading =
    dashboardRefItems.length > 0 &&
    (insuranceQueries.some(query => query.isLoading) ||
      pagedCoverageQueries.some(query => query.isLoading))

  const isSearchDataLoading =
    isSearchEnabled &&
    isSearchActive &&
    allInsuranceQueries.some(query => query.isLoading && !query.isFetched)

  const showUnmetMessage = hasUnmetRequirements && !dismissedUnmetMessage
  const showCanceledMessage = hasCancelled && !dismissedCanceledMessage
  const hasVisibleDashboardAlerts =
    showSuccessMessage || showUnmetMessage || showCanceledMessage

  const openHelp = useCallback(() => setNeedHelp(true), [])

  const handleSearchInputValueChange = useCallback(
    ({ inputValue }) => {
      const nextValue = inputValue ?? ''
      setSearchInputValue(nextValue)
      handleSearchInputCleared(nextValue)
    },
    [handleSearchInputCleared]
  )

  const handleSearchStateChange = useCallback(
    changes => {
      if (changes.inputValue === '') {
        handleSearchInputCleared('')
      }
    },
    [handleSearchInputCleared]
  )

  const handleSearchItemSelect = useCallback(
    ({ selectedItem }) => {
      if (selectedItem) {
        handleSearchSubmit(selectedItem)
      }
    },
    [handleSearchSubmit]
  )

  const handleRemoveRecentSearch = useCallback(item => {
    setRecentSearches(prev =>
      prev.filter(existingItem => existingItem !== item)
    )
  }, [])

  useEffect(() => {
    activeSearchTermRef.current = activeSearchTerm
  }, [activeSearchTerm])

  useEffect(() => {
    setDismissedUnmetMessage(false)
    setDismissedCanceledMessage(false)
  }, [profileKey])

  useEffect(() => {
    setActiveSearchTerm(null)
    setSearchInputValue('')
    setPageParam(undefined, 'replaceIn')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardItemsKey])

  useEffect(() => {
    if (displayRefItems.length === 0) return
    if (!pageParam || pageParam < 1 || pageParam > totalPages) {
      setPageParam(undefined, 'replaceIn')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardItemsKey, totalPages, activeSearchTerm])

  return {
    authHydrationReady,
    state,
    userData,
    needHelp,
    setNeedHelp,
    showSuccessMessage,
    setShowSuccessMessage,
    showUnmetMessage,
    setDismissedUnmetMessage,
    showCanceledMessage,
    setDismissedCanceledMessage,
    profileRefIds,
    dashboardRefItems,
    displayRefItems,
    pagedRefItems,
    totalPages,
    currentPage,
    allInsuranceErrored,
    allCoverageErrored,
    allRefTilesErrored,
    isResolvingDashboardItems,
    isHeaderDataLoading,
    isSearchDataLoading,
    hasVisibleDashboardAlerts,
    recentSearches,
    searchInputValue,
    isSearchActive,
    openHelp,
    handleSearchSubmit,
    handleSearchInputValueChange,
    handleSearchStateChange,
    handleSearchItemSelect,
    handleRemoveRecentSearch,
    handlePageChange,
    dispatch,
    agencyId: AGENCY_ID,
    pageSize: PAGE_SIZE,
  }
}
